import { Workspace } from "@dto/workspaces";
import { reorderWorkspaces } from "@utils/model";
import { isAfterLastCard, isInsideRect, moveWorkspace, workspaceDropPosition, WorkspaceDropPosition } from "@utils/workspacesOrder";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore, useUserStore } from "src/store";

// Drag type of a workspace card of the home page
export const WORKSPACE_DRAG_TYPE = "application/x-compozz-workspace";

export interface WorkspaceDropTarget {
    // Workspace the dragged one is dropped next to
    key: string;
    position: WorkspaceDropPosition;
    // Layout of the list when the target was computed: the drop line is
    // horizontal in a single column, vertical otherwise
    singleColumn: boolean;
}

function sameTarget(a: WorkspaceDropTarget | null, b: WorkspaceDropTarget | null): boolean {
    return a?.key === b?.key && a?.position === b?.position && a?.singleColumn === b?.singleColumn;
}

/**
 * Drag & drop of the workspace cards of the home page, to change the display
 * order of the workspaces. The order is shared by every user of the tenant:
 * only the owners of the tenant can change it (the cards of the other users
 * are not draggable, and the server refuses their request anyway).
 * The list is reordered at once, then replaced by the workspaces returned by
 * the server; the previous order is restored when the server refuses.
 */
export function useWorkspacesReorder() {
    const { t } = useTranslation();
    const user = useUserStore(state => state.user);
    const workspaces = useModelStore(state => state.workspaces);
    const setWorkspaces = useModelStore(state => state.updateWorkspaces);
    const [draggedKey, setDraggedKey] = useState<string | null>(null);
    const [dropTarget, setDropTarget] = useState<WorkspaceDropTarget | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    // The handlers read the refs, the states are only there to render: a drop
    // may follow a drag-over before React has rendered the state it set
    const draggedKeyRef = useRef<string | null>(null);
    const dropTargetRef = useRef<WorkspaceDropTarget | null>(null);

    // One reordering at a time: the order to restore on failure stays unambiguous
    const canReorder = user?.owner === true && workspaces.length > 1 && !isSaving;

    const updateDropTarget = (target: WorkspaceDropTarget | null) => {
        if (!sameTarget(dropTargetRef.current, target)) {
            dropTargetRef.current = target;
            setDropTarget(target);
        }
    };

    const endDrag = () => {
        draggedKeyRef.current = null;
        setDraggedKey(null);
        updateDropTarget(null);
    };

    // Accepts the drop on the given target, unless it changes nothing: the
    // drop is then refused (no drop line, not-allowed cursor)
    const offerDropTarget = (e: React.DragEvent<HTMLElement>, target: WorkspaceDropTarget) => {
        const dragged = draggedKeyRef.current;
        if (!dragged || !moveWorkspace(workspaces, dragged, target.key, target.position)) {
            updateDropTarget(null);
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        updateDropTarget(target);
    };

    const handleCardDragStart = (e: React.DragEvent<HTMLElement>, workspace: Workspace) => {
        // The dialogs of the card are portaled, but their drag events (text
        // selection...) still bubble to the card through the React tree
        if (!canReorder || e.target !== e.currentTarget) {
            return;
        }
        e.dataTransfer.setData(WORKSPACE_DRAG_TYPE, workspace._key);
        e.dataTransfer.effectAllowed = "move";
        draggedKeyRef.current = workspace._key;
        setDraggedKey(workspace._key);
    };

    const handleCardDragOver = (e: React.DragEvent<HTMLElement>, workspace: Workspace) => {
        if (!draggedKeyRef.current) {
            return;
        }
        const card = e.currentTarget.getBoundingClientRect();
        const list = e.currentTarget.parentElement?.getBoundingClientRect();
        const singleColumn = !list || card.width > list.width / 2;
        const position = workspaceDropPosition(card, e.clientX, e.clientY, singleColumn);
        offerDropTarget(e, { key: workspace._key, position, singleColumn });
    };

    // Drag-over of the list itself, not of one of its cards
    const handleListDragOver = (e: React.DragEvent<HTMLElement>) => {
        if (!draggedKeyRef.current || e.target !== e.currentTarget) {
            return;
        }
        const lastCard = e.currentTarget.lastElementChild?.getBoundingClientRect();
        const lastWorkspace = workspaces[workspaces.length - 1];
        if (lastCard && lastWorkspace && isAfterLastCard(lastCard, e.clientX, e.clientY)) {
            // Empty space which follows the last card: the end of the list
            const singleColumn = lastCard.width > e.currentTarget.getBoundingClientRect().width / 2;
            offerDropTarget(e, { key: lastWorkspace._key, position: 'after', singleColumn });
        } else if (dropTargetRef.current) {
            // Between two cards: the last target is kept, the drop line sits there
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
        }
    };

    const handleListDragLeave = (e: React.DragEvent<HTMLElement>) => {
        // Leaving a card for another one also bubbles here: the target is only
        // forgotten when the pointer leaves the list (the related target of
        // the event cannot tell, some browsers do not give it)
        if (!isInsideRect(e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY)) {
            updateDropTarget(null);
        }
    };

    const handleListDrop = (e: React.DragEvent<HTMLElement>) => {
        const dragged = draggedKeyRef.current;
        const target = dropTargetRef.current;
        if (!dragged) {
            return;
        }
        e.preventDefault();
        endDrag();
        const reordered = target ? moveWorkspace(workspaces, dragged, target.key, target.position) : null;
        if (!reordered) {
            return;
        }

        const previous = workspaces;
        setWorkspaces(reordered);
        setIsSaving(true);
        reorderWorkspaces(
            reordered.map(workspace => workspace._key),
            (saved) => {
                setWorkspaces(saved);
                setIsSaving(false);
            },
            () => {
                setWorkspaces(previous);
                setIsSaving(false);
            },
            t
        );
    };

    const listDragProps = {
        onDragOver: handleListDragOver,
        onDragLeave: handleListDragLeave,
        onDrop: handleListDrop,
    };

    const cardDragProps = (workspace: Workspace) => ({
        draggable: canReorder,
        onDragStart: (e: React.DragEvent<HTMLElement>) => handleCardDragStart(e, workspace),
        onDragEnd: endDrag,
        onDragOver: (e: React.DragEvent<HTMLElement>) => handleCardDragOver(e, workspace),
    });

    return { draggedKey, dropTarget, listDragProps, cardDragProps };
}
